import React, { useState, useEffect } from "react";
import useCRUD from "../../hooks/useCRUD";
import AddOrEditParticipantModal from "../EventDashboard/AddorEditParticipantModal";
import Spinner from "../Spinner/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faUsers,
  faMobileScreen,
  faMapPin,
  faLocationDot,
  faMapLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import GeocodePromptModal from "../Elements/GeocodePromptModal";
import ParticipantsMapModal from "../EventDashboard/ParticipantsMapModal";
import NotificationPauseModal from "./NotificationPauseModal";
import {
  geocodeParticipant,
  geocodeGroup,
  fetchAccountSettings,
  hasParticipantCoords,
} from "../../utils/geocode";
import { formatPhoneNumber } from "../../utils/phoneNumberUtils";

const Participants = ({
  participants,
  refreshData,
  event,
  groupId,
  isRSVPOnly,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [pauseParticipant, setPauseParticipant] = useState(null);

  const {
    addItem,
    updateItem,
    deleteItem,
    error: crudError,
  } = useCRUD("/api/participants");

  const [sortedParticipants, setSortedParticipants] = useState([]);

  const [error, setError] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "descending",
  });

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleAddNewMemberClick = () => {
    console.log("Opening modal...");

    setIsModalOpen(true); // This should open the modal
    console.log("Modal should now be open, isModalOpen:", isModalOpen);

    setModalData(null); // Initialize data for new member
  };

  const handleEditClick = (participant) => {
    setIsModalOpen(true);
    setModalData({ ...participant, isDriver: false }); // Set modal for editing a member
  };

  const currentNotificationPause = (participant) =>
    participant.activeNotificationPause ||
    participant.upcomingNotificationPause;

  const handleRemoveNotificationPause = async (participant) => {
    const pause = currentNotificationPause(participant);
    if (!pause) return;

    if (!window.confirm(`Remove RSVP pause for ${participant.name}?`)) return;

    try {
      setIsLoading(true);
      const token = localStorage.getItem("userToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/participant-notification-pauses/${pause.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) throw new Error("Could not remove RSVP pause.");
      refreshData();
    } catch (error) {
      alert(error.message || "Could not remove RSVP pause.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      setIsLoading(true);
      setError(""); // Reset error message on new save attempt

      let response; // This will store the response from your save functions

      if (modalData) {
        // Editing an existing participant
        response = await updateItem(modalData.id, data);
      } else {
        // Adding a new participant
        data.participantGroupId = groupId;
        response = await addItem(data);
      }
      console.log("response", response);

      // Check the response to ensure it was successful
      // This assumes your addItem/updateItem functions return some success indicator
      // Adjust the following line according to your actual API response structure
      if (response && response.data) {
        console.log("Participant saved successfully:", data);
        // Optionally, refresh your participants list here
      } else {
        // Handle unsuccessful save
        // Adjust the following line according to your actual API response structure
        throw new Error(
          response.message || "An error occurred while saving the participant.",
        );
      }
    } catch (error) {
      console.error("Error saving participant:", error);
      console.log("crudError", crudError);
      setError(error.message || "Error saving participant. Please try again.");
    } finally {
      setIsModalOpen(false); // Close the modal after attempting save
      refreshData(); // Refresh data regardless of success/failure
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (participants) {
      let sortedParticipants = [...participants];
      if (sortConfig.key !== null) {
        sortedParticipants.sort((a, b) => {
          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === "ascending" ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === "ascending" ? 1 : -1;
          }
          return 0;
        });
      }

      setSortedParticipants(sortedParticipants);
    }
  }, [participants, sortConfig]);

  const handleDeleteClick = async (participant) => {
    try {
      setIsLoading(true);
      await deleteItem(participant.id);
      console.log("Participant deleted successfully:", participant);
      // Optionally, refresh your participants list here
    } catch (error) {
      console.error("Error deleting participant:", error);
    } finally {
      refreshData();
      setIsLoading(false);
    }
  };

  const apiBaseUrl = process.env.REACT_APP_API_URL;

  const handleInviteToApp = async (participant) => {
    if (
      !window.confirm(`Send an app download invite SMS to ${participant.name}?`)
    ) {
      return;
    }
    try {
      setIsLoading(true);
      const token = localStorage.getItem("userToken");
      const response = await fetch(
        `${apiBaseUrl}/api/participants/${participant.id}/invite-to-app`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.message || "Failed to send invite.");
      }
      alert("Invite sent.");
    } catch (err) {
      console.error("Error sending invite:", err);
      alert(err.message || "Failed to send invite.");
    } finally {
      setIsLoading(false);
    }
  };

  const mobileUserCount = (participants || []).filter(
    (p) => p.hasMobileApp,
  ).length;

  const [geocodeModal, setGeocodeModal] = useState({
    open: false,
    participant: null,
    bulk: false,
  });

  const runGeocodeForParticipant = async (participant, extras = {}) => {
    try {
      setIsLoading(true);
      await geocodeParticipant(participant.id, extras);
      refreshData();
    } catch (err) {
      console.error("Geocode failed:", err);
      alert(err.message || "Failed to fetch GPS coordinates.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFillGps = async (participant) => {
    try {
      const account = await fetchAccountSettings();
      const hasBase = !!(
        account?.baseCity ||
        account?.baseState ||
        account?.baseCountry
      );
      if (hasBase) {
        await runGeocodeForParticipant(participant);
      } else {
        setGeocodeModal({ open: true, participant, bulk: false });
      }
    } catch (err) {
      console.error(err);
      setGeocodeModal({ open: true, participant, bulk: false });
    }
  };

  const handleFillAllGps = async () => {
    try {
      const account = await fetchAccountSettings();
      const hasBase = !!(
        account?.baseCity ||
        account?.baseState ||
        account?.baseCountry
      );
      if (hasBase) {
        await runGroupGeocode({});
      } else {
        setGeocodeModal({ open: true, participant: null, bulk: true });
      }
    } catch (err) {
      console.error(err);
      setGeocodeModal({ open: true, participant: null, bulk: true });
    }
  };

  const runGroupGeocode = async (extras = {}) => {
    try {
      setIsLoading(true);
      const result = await geocodeGroup(groupId, extras);
      alert(
        result?.message || `Geocoded ${result?.updated || 0} participant(s).`,
      );
      refreshData();
    } catch (err) {
      console.error("Bulk geocode failed:", err);
      alert(err.message || "Failed to geocode group.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeocodeModalSubmit = async ({ city, state, country }) => {
    const extras = { city, state, country };
    const { participant, bulk } = geocodeModal;
    setGeocodeModal({ open: false, participant: null, bulk: false });
    if (bulk) {
      await runGroupGeocode(extras);
    } else if (participant) {
      await runGeocodeForParticipant(participant, extras);
    }
  };

  const missingCoordsCount = (participants || []).filter(
    (p) => p.rideLocation && !hasParticipantCoords(p),
  ).length;

  const withCoordsCount = (participants || []).filter((p) =>
    hasParticipantCoords(p),
  ).length;

  const [isMapOpen, setIsMapOpen] = useState(false);

  return (
    <div className="card rounded-xl border border-gray-100 bg-white shadow-lg">
      {isLoading && <Spinner />}

      {error && <p className="text-red-500">{error}</p>}

      <div className="card-body">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#004a7e]">
              Group members
            </p>
            <h3 className="text-xl font-black text-gray-900">
              {participants?.length} Event Members
            </h3>
            {participants?.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                <FontAwesomeIcon icon={faMobileScreen} className="mr-1" />
                {mobileUserCount} on mobile app
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {!isRSVPOnly && missingCoordsCount > 0 && (
              <button
                className="rounded-lg border border-[#004a7e] bg-white px-3 py-2 text-sm font-bold text-[#004a7e] hover:bg-[#004a7e] hover:text-white"
                onClick={handleFillAllGps}
                title={`Geocode ${missingCoordsCount} member(s) missing GPS coordinates`}
              >
                <FontAwesomeIcon icon={faLocationDot} className="mr-1" />
                Fill in GPS ({missingCoordsCount})
              </button>
            )}
            {!isRSVPOnly && withCoordsCount > 0 && (
              <button
                className="rounded-lg border border-[#004a7e] bg-white px-3 py-2 text-sm font-bold text-[#004a7e] hover:bg-[#004a7e] hover:text-white"
                onClick={() => setIsMapOpen(true)}
                title={`Plot ${withCoordsCount} member(s) on a map`}
              >
                <FontAwesomeIcon icon={faMapLocationDot} className="mr-1" />
                View on Map
              </button>
            )}
            <button
              className="rounded-lg bg-[#004a7e] px-3 py-2 text-sm font-bold text-white hover:bg-[#003a64]"
              onClick={handleAddNewMemberClick}
            >
              + Add New Member
            </button>
          </div>
          {isModalOpen && (
            <AddOrEditParticipantModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              initialData={modalData}
              isDriver={false}
              onSave={handleSave}
              event={event}
              isRSVPOnly={isRSVPOnly}
            />
          )}
        </div>

        {sortedParticipants?.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-gray-100">
            <table className="table w-full hidden md:table">
              <thead>
                <tr>
                  <th onClick={() => requestSort("name")}>
                    Member{" "}
                    {sortConfig.key === "name" &&
                      (sortConfig.direction === "ascending" ? "▲" : "▼")}
                  </th>

                  {!isRSVPOnly && (
                    <th onClick={() => requestSort("rideLocation")}>
                      Pickup Location{" "}
                      {sortConfig.key === "rideLocation" &&
                        (sortConfig.direction === "ascending" ? "▲" : "▼")}
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {sortedParticipants.map((participant) => (
                  <tr key={participant.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="font-bold">
                            {participant.name}
                            {participant.hasMobileApp && (
                              <span
                                className="ml-2 bg-green-600 text-white py-1 px-2 text-xs rounded"
                                title="Using mobile app"
                              >
                                <FontAwesomeIcon icon={faMobileScreen} /> Mobile
                              </span>
                            )}
                            {participant.pauseNotifications && (
                              <span className="ml-2 bg-red-500 text-white py-1 px-2 text-xs rounded">
                                Paused
                              </span>
                            )}
                            {participant.activeNotificationPause && (
                              <span className="ml-2 bg-orange-500 text-white py-1 px-2 text-xs rounded">
                                RSVP paused until{" "}
                                {participant.activeNotificationPause.endsAt}
                              </span>
                            )}
                            {participant.upcomingNotificationPause && (
                              <span className="ml-2 bg-orange-100 text-orange-700 py-1 px-2 text-xs rounded">
                                Pause starts{" "}
                                {participant.upcomingNotificationPause.startsAt}
                              </span>
                            )}
                            {participant.noOfPpl > 1 && (
                              <span className="ml-2 bg-[#004a7e] text-white py-1 px-2  text-xs">
                                {participant.noOfPpl}{" "}
                                <FontAwesomeIcon icon={faUsers} />
                              </span>
                            )}
                          </div>
                          <div className="text-sm opacity-50">
                            {formatPhoneNumber(participant.phone)}
                          </div>
                        </div>
                      </div>
                    </td>
                    {!isRSVPOnly && (
                      <td>
                        <div className="flex items-center gap-2">
                          <span>{participant.rideLocation}</span>
                          {hasParticipantCoords(participant) && (
                            <span
                              className="text-green-600"
                              title={
                                participant.pickupGeocodedAddress ||
                                "GPS coordinates saved"
                              }
                            >
                              <FontAwesomeIcon icon={faMapPin} />
                            </span>
                          )}
                        </div>
                      </td>
                    )}

                    <td>
                      <div className="dropdown dropdown-top">
                        <button
                          tabIndex="0"
                          className="btn btn-ghost btn-xs m-1"
                        >
                          ...
                        </button>
                        <ul
                          tabIndex="0"
                          className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52"
                        >
                          <li>
                            <button
                              onClick={() => handleEditClick(participant)}
                            >
                              Edit
                            </button>
                          </li>
                          {!isRSVPOnly && participant.rideLocation && (
                            <li>
                              <button
                                onClick={() => handleFillGps(participant)}
                              >
                                <FontAwesomeIcon icon={faLocationDot} />
                                {hasParticipantCoords(participant)
                                  ? " Refresh GPS"
                                  : " Fill in GPS"}
                              </button>
                            </li>
                          )}
                          {!participant.hasMobileApp && (
                            <li>
                              <button
                                onClick={() => handleInviteToApp(participant)}
                              >
                                <FontAwesomeIcon icon={faMobileScreen} /> Invite
                                to App
                              </button>
                            </li>
                          )}
                          <li>
                            <button
                              onClick={() => setPauseParticipant(participant)}
                            >
                              Pause RSVP texts
                            </button>
                          </li>
                          {currentNotificationPause(participant) && (
                            <li>
                              <button
                                onClick={() =>
                                  handleRemoveNotificationPause(participant)
                                }
                              >
                                Remove RSVP pause
                              </button>
                            </li>
                          )}
                          <li>
                            <button
                              onClick={() => handleDeleteClick(participant)}
                            >
                              Delete
                            </button>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="md:hidden">
              {sortedParticipants.map((participant) => (
                <div
                  key={participant.id}
                  className="rounded-lg border border-gray-100 bg-white p-4 mb-3 flex items-center relative shadow-sm"
                >
                  <div className="flex-grow">
                    <a
                      href={`sms:${participant.phone}`}
                      className="font-bold hover:text-blue-700"
                    >
                      {participant.name}{" "}
                      {participant.hasMobileApp && (
                        <span
                          className="ml-2 bg-green-600 text-white py-1 px-2 text-xs rounded"
                          title="Using mobile app"
                        >
                          <FontAwesomeIcon icon={faMobileScreen} /> Mobile
                        </span>
                      )}
                      {participant.pauseNotifications && (
                        <span className="ml-2 bg-red-500 text-white py-1 px-2 text-xs rounded">
                          Paused
                        </span>
                      )}
                      {participant.activeNotificationPause && (
                        <span className="ml-2 bg-orange-500 text-white py-1 px-2 text-xs rounded">
                          RSVP paused until{" "}
                          {participant.activeNotificationPause.endsAt}
                        </span>
                      )}
                      {participant.upcomingNotificationPause && (
                        <span className="ml-2 bg-orange-100 text-orange-700 py-1 px-2 text-xs rounded">
                          Pause starts{" "}
                          {participant.upcomingNotificationPause.startsAt}
                        </span>
                      )}
                      {participant.noOfPpl > 1 && (
                        <span className="ml-2 bg-[#004a7e] text-white py-1 px-2  text-xs">
                          {participant.noOfPpl}{" "}
                          <FontAwesomeIcon icon={faUsers} />
                        </span>
                      )}
                    </a>
                    <div className="text-sm text-gray-500">
                      {formatPhoneNumber(participant.phone)}
                    </div>
                    {!isRSVPOnly && (
                      <div className="text-sm text-gray-500 flex items-center gap-2">
                        <span>{participant.rideLocation}</span>
                        {hasParticipantCoords(participant) && (
                          <span
                            className="text-green-600"
                            title={
                              participant.pickupGeocodedAddress ||
                              "GPS coordinates saved"
                            }
                          >
                            <FontAwesomeIcon icon={faMapPin} />
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="dropdown dropdown-end">
                    <button tabIndex="0" className="btn btn-ghost btn-xs m-1">
                      ...
                    </button>
                    <ul
                      tabIndex="0"
                      className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52 z-30"
                    >
                      <li>
                        <a
                          href="#"
                          onClick={() => handleEditClick(participant)}
                          className="justify-between"
                        >
                          Edit
                        </a>
                      </li>
                      {!isRSVPOnly && participant.rideLocation && (
                        <li>
                          <button onClick={() => handleFillGps(participant)}>
                            <FontAwesomeIcon icon={faLocationDot} />
                            {hasParticipantCoords(participant)
                              ? " Refresh GPS"
                              : " Fill in GPS"}
                          </button>
                        </li>
                      )}
                      {!participant.hasMobileApp && (
                        <li>
                          <button
                            onClick={() => handleInviteToApp(participant)}
                          >
                            <FontAwesomeIcon icon={faMobileScreen} /> Invite to
                            App
                          </button>
                        </li>
                      )}
                      <li>
                        <button
                          onClick={() => setPauseParticipant(participant)}
                        >
                          Pause RSVP texts
                        </button>
                      </li>
                      {currentNotificationPause(participant) && (
                        <li>
                          <button
                            onClick={() =>
                              handleRemoveNotificationPause(participant)
                            }
                          >
                            Remove RSVP pause
                          </button>
                        </li>
                      )}
                      <li>
                        <button onClick={() => handleDeleteClick(participant)}>
                          Delete
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="rounded-lg bg-gray-50 p-5 text-center text-gray-600">
            No participants to display.
          </p>
        )}
      </div>

      <GeocodePromptModal
        isOpen={geocodeModal.open}
        onClose={() =>
          setGeocodeModal({ open: false, participant: null, bulk: false })
        }
        onSubmit={handleGeocodeModalSubmit}
        title={
          geocodeModal.bulk
            ? "Fill in GPS for all members"
            : `Fill in GPS for ${geocodeModal.participant?.name || "member"}`
        }
        description="Your account doesn't have a default city/state/country yet. Provide it here so we can resolve partial pickup addresses. You can save it permanently in Settings → Default Location."
      />

      <ParticipantsMapModal
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        participants={participants}
      />

      <NotificationPauseModal
        participant={pauseParticipant}
        onClose={() => setPauseParticipant(null)}
        onSaved={refreshData}
      />
    </div>
  );
};

export default Participants;
